import { PaperclipIcon } from "@storytell/bsky-icons";
import { isStreaming } from "@storytell/streaming";
import clsx from "clsx";
import { type Component, createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import { ButtonCircleIcon } from "../ui";
import type { PromptBarProps } from "./PromptBar.types";
import { PromptBarProvider, usePromptBarContext } from "./internal/PromptBarContext";
import {
  CreditsDisplay,
  FileDropzone,
  GradientBorder,
  KnowledgeSourcesDisplay,
  ModeSelector,
  ScopeDisplay,
  StatusDisplay,
  SubmitButton,
} from "./internal/components";
import { useDragAndDrop } from "./internal/hooks";
import { PromptEditor } from "@storytell/rich-text";
import { PromptBarButton } from "./internal/components/PromptBarButton";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { subscribeToGlobalEvent } from "@storytell/events";
import { twMerge } from "tailwind-merge";
import { TbQuote, TbX } from "solid-icons/tb";
import { PromptBarQuote } from "./internal/components/PromptBarQuote";
import { useFileUpload } from "~/lib/file-upload";
import { useWire } from "~/wire";

/**
 * Internal component that renders the actual prompt bar UI
 */
const PromptBarContent: Component = () => {
  const context = usePromptBarContext();
  const wire = useWire();

  const [quote, setQuote] = createSignal("");
  onMount(() => {
    const unsubscribeQuote = subscribeToGlobalEvent("quote-in-prompt-bar", (ev) => {
      setQuote(ev.detail?.text ?? "");
    });

    const unsubscribeFilePicker = subscribeToGlobalEvent("open-file-picker", () => {
      handlePaperclipClick();
    });

    const unsubscribePromptSubmittedSuccessfully = subscribeToGlobalEvent("prompt-submitted-successfuly", () => {
      context.prompt.editor()?.commands.setContent([]);
    });

    onCleanup(() => {
      unsubscribeQuote();
      unsubscribeFilePicker();
      unsubscribePromptSubmittedSuccessfully();
    });
  });

  const { isDropzoneMode, isInSubmittedMode, handleDragOver, handleDragLeave, handleDrop, fileUpload } =
    useDragAndDrop();

  const maxCharacters = 3000;
  const showCountThreshold = 500;
  const shouldShowCount = () => context.input.characterCount() >= showCountThreshold;

  const handlePaperclipClick = () => {
    // Create a file input element
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "*/*"; // Accept all file types
    input.style.display = "none";

    // Add to DOM temporarily
    document.body.appendChild(input);

    // Handle file selection
    input.addEventListener("change", async (event) => {
      const files = Array.from((event.target as HTMLInputElement).files || []);

      if (files.length > 0) {
        try {
          // Create temporary asset entries for immediate UI feedback
          const tempAssets = files.map((file) => {
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            context.scope.addAsset(tempId, file.name, "uploading");
            return { tempId, filename: file.name };
          });

          // Upload files
          await fileUpload.uploadFiles(files, wire.auth.realm().projectId);

          // Clean up any remaining temporary assets
          tempAssets.forEach(({ tempId, filename }) => {
            const stillExists = context.scope.assets().some((asset) => asset.assetId === tempId);
            if (stillExists) {
              context.scope.removeAsset(tempId);
            }
          });
        } catch (error) {
          console.error("Failed to upload files:", error);

          // Update any remaining temporary assets to error state
          const tempAssets = files.map((file) => {
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            context.scope.addAsset(tempId, file.name, "uploading");
            return { tempId };
          });

          tempAssets.forEach(({ tempId }) => {
            const stillExists = context.scope.assets().some((asset) => asset.assetId === tempId);
            if (stillExists) {
              context.scope.updateAssetStatus(tempId, "error");
            }
          });
        }
      }

      // Clean up the input element
      document.body.removeChild(input);
    });

    // Open file picker
    input.click();
  };

  return (
    <div class="w-full max-w-4xl mx-auto relative">
      <GradientBorder />
      {/* Main Prompt Bar */}
      <div>
        <div
          class={clsx(
            "relative transition-all duration-300 rounded-3xl",
            isDropzoneMode()
              ? [
                  "border-2 border-primary/40 dark:border-dark-primary/40",
                  "border-dashed",
                  "bg-gradient-to-br from-black/15 via-black/8 to-black/3 dark:from-black/30 dark:via-black/20 dark:to-black/10",
                ]
              : ["bg-panel", "shadow-lg", "shadow-black/10 dark:shadow-black/30"],
          )}
          style={
            isDropzoneMode()
              ? {
                  "box-shadow": `
                inset 0 6px 20px rgba(0,0,0,0.4),
                inset 0 3px 12px rgba(0,0,0,0.3),
                inset 0 1px 6px rgba(0,0,0,0.2),
                inset 0 -1px 3px rgba(255,255,255,0.08),
                inset 0 0 0 1px rgba(0,0,0,0.1)
              `,
                }
              : undefined
          }
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Normal Input State */}
          <div
            class={twMerge(
              "pt-4 pb-3 space-y-4 transition-opacity duration-300",
              isDropzoneMode() ? "opacity-0 pointer-events-none" : "opacity-100",
              quote() && "pt-0",
            )}
          >
            <Show when={quote()}>
              <PromptBarQuote quote={quote()} onClose={() => setQuote("")} />
            </Show>

            {/* Scope Display */}
            <ScopeDisplay />

            {/* Text Input */}
            <div class="px-6">
              <PromptEditor context={context.prompt} />
            </div>

            {/* Action Bar - Icons and Submit */}
            <div data-testid="prompt-action-bar" class="flex items-center justify-between px-6">
              {/* Left Side Icons */}
              <div class="flex items-center gap-2">
                <StTooltip
                  content={<p>Upload files or connect data sources.</p>}
                  placement="top"
                  ignoreSafeArea
                  dontKeepOpenOnHover
                  contentProps={{
                    class: "z-10",
                  }}
                >
                  <PromptBarButton
                    as={StTooltipTrigger}
                    aria-label="Upload files"
                    // active={isDropzoneMode()}
                    icon={PaperclipIcon}
                    onClick={handlePaperclipClick}
                    type="button"
                  />
                </StTooltip>

                {/* Character Count Indicator */}
                <Show when={shouldShowCount()}>
                  <div class="text-xs text-muted-text px-2">
                    {context.input.characterCount()}/{maxCharacters}
                  </div>
                </Show>
              </div>

              {/* Right Side */}
              <div class="flex items-center gap-4">
                <Switch>
                  <Match when={wire.chat.isChatStreaming()}>
                    <StatusDisplay />
                  </Match>
                  <Match when={!wire.chat.isChatStreaming()}>
                    <CreditsDisplay />
                  </Match>
                </Switch>
                <KnowledgeSourcesDisplay />
                <SubmitButton />
              </div>
            </div>
          </div>

          {/* File Dropzone Overlay */}
          <div
            class={clsx(
              "transition-opacity duration-300",
              isDropzoneMode() ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <FileDropzone fileUpload={fileUpload} />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Main PromptBar component - the public API
 *
 * A sophisticated prompt input interface with:
 * - Dynamic mode switching (chat/agent/workflow)
 * - File drag and drop support
 * - Credit display and management
 * - Animated gradient border
 * - Responsive submit button (mic/arrow/stop)
 * - Character counting and limits
 * - Streaming state awareness for UI transformations
 */
export const PromptBar: Component<PromptBarProps> = (props) => {
  return (
    <PromptBarProvider
      onSubmit={props.onSubmit}
      streamingState={props.streamingState}
      onStopStreaming={props.onStopStreaming}
    >
      <PromptBarContent />
    </PromptBarProvider>
  );
};
